import React, { useState, useCallback, useEffect } from "react";
import {
  GoogleMap,
  useLoadScript,
  Marker,
  DirectionsRenderer,
  OverlayView,
} from "@react-google-maps/api";
import BusIcon from "../../images/vehicle/Map_Vehicle_active.svg";
import config from "../../config.json";
import useSSE from "../../hooks/useSSE.js";
import DeadBusIcon from "../../images/vehicle/Map_Vehicle_dead.svg";
import InactiveBusIcon from "../../images/vehicle/Map_Vehicle_inactive.svg";

export const containerStyle = {
  border: "1px solid #183059",
  padding: "40px",
  height: "426px",
  width: "100%",
};

const center = { lat: 13.1986, lng: 77.7066 };

const libraries = ["places"];

const colors = [
  "#20A8E0",
  "#8FBC8F",
  "#FFD700",
  "#6495ED",
  "#FF69B4",
  "#FFA07A",
  "#ADFF2F",
  "#FF4500",
  "#9370DB",
  "#FFFFFF",
];

const mapStyles = [
  {
    featureType: "all",
    elementType: "all",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "road",
    elementType: "geometry",
    stylers: [{ visibility: "on" }, { color: "black" }],
  },
];

const POSITION_HISTORY_SIZE = 20;
const MOVEMENT_THRESHOLD = 50; // meters
const TERMINAL_THRESHOLD = 60; // meters
const ROUTE_PROXIMITY_THRESHOLD = 100; // meters

const calculateDistance = (p1, p2) => {
  if (!p1 || !p2) return Infinity;
  const R = 6371e3;
  const φ1 = (p1.latitude * Math.PI) / 180;
  const φ2 = (p2.latitude * Math.PI) / 180;
  const Δφ = ((p2.latitude - p1.latitude) * Math.PI) / 180;
  const Δλ = ((p2.longitude - p1.longitude) * Math.PI) / 180;
  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

const detectMovement = (newPos, lastPos) => {
  if (!lastPos || !newPos) return true;
  const distance = calculateDistance(
    { latitude: newPos.latitude, longitude: newPos.longitude },
    { latitude: lastPos.latitude, longitude: lastPos.longitude }
  );
  console.log(`[${newPos.deviceId}] Movement check: ${distance} meters`);
  return distance > MOVEMENT_THRESHOLD;
};

const normalizeDeviceData = (data) => {
  if (data.deviceId && data.latitude && data.longitude) {
    return data;
  }
  return {
    deviceId: data.device_id,
    latitude: data.Latitude,
    longitude: data.Longitude,
    timestamp: new Date().toISOString(),
    rawData: data.RawData || "",
  };
};

const determineVehicleDirection = (deviceId, currentPosition, stops, routes, vehicles) => {
  const storageKey = `${deviceId}_tracking`;
  const storedData = JSON.parse(localStorage.getItem(storageKey) || "{}");
  let positionHistory = storedData.positions || [];
  let lastMovedPosition = storedData.lastMovedPosition || null;
  let pathDirection = storedData.direction || "unknown";

  console.log(`[${deviceId}] History: ${positionHistory.length}, Direction: ${pathDirection}, Position: (${currentPosition.latitude}, ${currentPosition.longitude})`);

  const isMoving = detectMovement(currentPosition, lastMovedPosition);
  if (isMoving) {
    positionHistory.push(currentPosition);
    lastMovedPosition = currentPosition;
    if (positionHistory.length > POSITION_HISTORY_SIZE) {
      positionHistory = positionHistory.slice(-POSITION_HISTORY_SIZE);
    }
  }

  const saveToStorage = () => {
    localStorage.setItem(storageKey, JSON.stringify({
      positions: positionHistory,
      lastMovedPosition,
      direction: pathDirection,
      timestamp: Date.now(),
      deviceId,
    }));
  };

  if (!stops || stops.length < 2 || !routes || routes.length === 0) {
    console.warn(`[${deviceId}] Invalid data: stops=${stops?.length}, routes=${routes?.length}`);
    saveToStorage();
    return pathDirection;
  }

  const vehicle = vehicles.find((v) => v.device_id === deviceId);
  if (!vehicle || !vehicle.routeId) {
    console.warn(`[${deviceId}] No vehicle or routeId found: vehicle=${JSON.stringify(vehicle)}`);
    saveToStorage();
    return pathDirection;
  }

  const vehicleRoute = routes.find((r) => r.id === vehicle.routeId);
  if (!vehicleRoute || !vehicleRoute.routeCoordinates || vehicleRoute.routeCoordinates.length === 0) {
    console.warn(`[${deviceId}] No valid route found: routeId=${vehicle.routeId}, route=${JSON.stringify(vehicleRoute)}`);
    saveToStorage();
    return pathDirection;
  }

  const isOnRoute = vehicleRoute.routeCoordinates.some(coord =>
    calculateDistance(
      { latitude: parseFloat(coord.latitude), longitude: parseFloat(coord.longitude) },
      currentPosition
    ) < ROUTE_PROXIMITY_THRESHOLD
  );
  if (!isOnRoute) {
    console.warn(`[${deviceId}] Vehicle not on route: min distance > ${ROUTE_PROXIMITY_THRESHOLD}m`);
    saveToStorage();
    return pathDirection;
  }

  const source = {
    latitude: parseFloat(stops[0].latitude),
    longitude: parseFloat(stops[0].longitude),
  };
  const destination = {
    latitude: parseFloat(stops[stops.length - 1].latitude),
    longitude: parseFloat(stops[stops.length - 1].longitude),
  };

  if (!pathDirection || pathDirection === "unknown") {
    const sourceDistance = calculateDistance(currentPosition, source);
    const destDistance = calculateDistance(currentPosition, destination);
    if (sourceDistance < destDistance) {
      pathDirection = "source_to_dest";
      console.log(`[${deviceId}] Initial direction set to source_to_dest`);
    } else {
      pathDirection = "dest_to_source";
      console.log(`[${deviceId}] Initial direction set to dest_to_source`);
    }
  }

  const sourceDistance = calculateDistance(currentPosition, source);
  const destDistance = calculateDistance(currentPosition, destination);
  const nearSource = sourceDistance < TERMINAL_THRESHOLD;
  const nearDest = destDistance < TERMINAL_THRESHOLD;

  console.log(`[${deviceId}] Source distance: ${sourceDistance}m, Dest distance: ${destDistance}m, Near source: ${nearSource}, Near dest: ${nearDest}`);

  if (nearSource && pathDirection !== "source_to_dest") {
    console.log(`[${deviceId}] At source, setting source_to_dest`);
    pathDirection = "source_to_dest";
    positionHistory = [currentPosition];
  } else if (nearDest && pathDirection !== "dest_to_source") {
    console.log(`[${deviceId}] At destination, setting dest_to_source`);
    pathDirection = "dest_to_source";
    positionHistory = [currentPosition];
  } else if (isMoving && positionHistory.length >= POSITION_HISTORY_SIZE) {
    let sourceDistanceChange = 0;
    let destDistanceChange = 0;
    for (let i = 1; i < positionHistory.length; i++) {
      const prevPos = positionHistory[i - 1];
      const currPos = positionHistory[i];
      sourceDistanceChange += calculateDistance(currPos, source) - calculateDistance(prevPos, source);
      destDistanceChange += calculateDistance(currPos, destination) - calculateDistance(prevPos, destination);
    }
    console.log(`[${deviceId}] 15-point trend: sourceΔ=${sourceDistanceChange}, destΔ=${destDistanceChange}`);
    let newDirection = null;
    if (sourceDistanceChange < 0 && destDistanceChange > 0) {
      newDirection = "dest_to_source";
    } else if (sourceDistanceChange > 0 && destDistanceChange < 0) {
      newDirection = "source_to_dest";
    }
    if (newDirection && newDirection !== pathDirection) {
      console.log(`[${deviceId}] Updating to ${newDirection} via 15-point trend`);
      pathDirection = newDirection;
      positionHistory = positionHistory.slice(-3);
    }
  }

  saveToStorage();
  return pathDirection;
};

function Map({ stops, routes, vehicles }) {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: "AIzaSyD7rqUpTzUpEbxm-Xc7ikltFJGzOhd92Qk",
    libraries,
  });

  const [map, setMap] = useState(null);
  const [directions, setDirections] = useState([]);
  const [vehicleIds, setVehicleIds] = useState([]);
  const [vehicleLocations, setVehicleLocations] = useState([]);
  const [vehicleDirections, setVehicleDirections] = useState({});

  useEffect(() => {
    console.log("Vehicles data:", vehicles);
    const deviceIds = vehicles.map((v) => v.device_id);
    setVehicleIds(deviceIds);
    console.log("Vehicle IDs set:", deviceIds);
  }, [vehicles]);

  const handleSSEMessage = useCallback((data) => {
    console.log("SSE data received:", data);
    if (data.type && data.type === "connected") {
      console.log("Received connection confirmation:", data);
      return;
    }

    if (data.device_id && data.Latitude && data.Longitude) {
      const normalizedData = normalizeDeviceData(data);

      setVehicleLocations((prev) => {
        const existingIndex = prev.findIndex(
          (item) => item.deviceId === normalizedData.deviceId
        );
        if (existingIndex >= 0) {
          const newLocations = [...prev];
          newLocations[existingIndex] = normalizedData;
          return newLocations;
        }
        return [...prev, normalizedData];
      });

      const direction = determineVehicleDirection(
        normalizedData.deviceId,
        normalizedData,
        stops,
        routes,
        vehicles
      );

      setVehicleDirections((prev) => ({
        ...prev,
        [normalizedData.deviceId]: direction,
      }));
    } else {
      console.warn("Received SSE data in unexpected format:", data);
    }
  }, [stops, routes, vehicles]);

  useSSE(vehicleIds, config.baseURL, handleSSEMessage);

  const onLoad = useCallback(
    (map) => {
      setMap(map);
      map.setCenter({ lat: 13.1986, lng: 77.7099 });
      map.setZoom(16);
    },
    []
  );

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  useEffect(() => {
    if (isLoaded && routes.length > 0 && map) {
      const directionsService = new window.google.maps.DirectionsService();
      const fetchDirections = async () => {
        const directionsPromises = routes.map((route) => {
          return new Promise((resolve, reject) => {
            const waypoints = route.routeCoordinates
              .slice(1, -1)
              .map((coord) => ({
                location: new window.google.maps.LatLng(
                  parseFloat(coord.latitude),
                  parseFloat(coord.longitude)
                ),
                stopover: coord.isStop || false,
              }));

            const origin = new window.google.maps.LatLng(
              parseFloat(route.routeCoordinates[0].latitude),
              parseFloat(route.routeCoordinates[0].longitude)
            );
            const destination = new window.google.maps.LatLng(
              parseFloat(
                route.routeCoordinates[route.routeCoordinates.length - 1].latitude
              ),
              parseFloat(
                route.routeCoordinates[route.routeCoordinates.length - 1].longitude
              )
            );

            directionsService.route(
              {
                origin: origin,
                destination: destination,
                waypoints: waypoints,
                optimizeWaypoints: false,
                travelMode: window.google.maps.TravelMode.DRIVING,
              },
              (result, status) => {
                if (status === window.google.maps.DirectionsStatus.OK) {
                  resolve({ ...result, routeColor: route.routeColor });
                } else {
                  console.error(`Error fetching directions for route ${route.id}: ${status}`);
                  reject(`Error fetching directions: ${status}`);
                }
              }
            );
          });
        });

        try {
          const results = await Promise.all(directionsPromises);
          setDirections(results);
        } catch (error) {
          console.error("Error fetching directions:", error);
        }
      };

      fetchDirections();
    }
  }, [isLoaded, routes, map]);

  const getBackgroundColor = (deviceId) => {
    const index = Math.abs(hashString(deviceId)) % colors.length;
    return colors[index];
  };

  const hashString = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
      hash = hash & hash;
    }
    return hash;
  };

  const getDirectionArrow = (deviceId) => {
    const direction = vehicleDirections[deviceId];
    if (!direction || direction === "unknown") return "";
    return direction === "source_to_dest" ? " ➡️" : " ⬅️";
  };

  const getVehicleIcon = (status) => {
    switch (status) {
      case "DEAD":
        return DeadBusIcon;
      case "INACTIVE":
        return InactiveBusIcon;
      case "ACTIVE":
      default:
        return BusIcon;
    }
  };

  if (loadError) return <div>Error loading maps</div>;
  if (!isLoaded)
    return (
      <div style={{ padding: "40px" }}>
        <div height={400} width="100%" />
      </div>
    );

  return (
    <div style={{ padding: "40px" }}>
      <GoogleMap
        mapContainerStyle={containerStyle}
        onLoad={onLoad}
        onUnmount={onUnmount}
        options={{
          styles: mapStyles,
        }}
      >
        {stops.map((stop) => (
          <React.Fragment key={stop.id}>
            <OverlayView
              position={{
                lat: parseFloat(stop.latitude),
                lng: parseFloat(stop.longitude),
              }}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
            >
              <div
                style={{
                  position: "relative",
                  top: "-62px",
                  left: "0px",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    left: "50%",
                    transform: "translateX(-50%)",
                    backgroundColor: "white",
                    color: "black",
                    padding: "4px 8px",
                    borderRadius: "4px",
                    fontSize: "12px",
                    fontWeight: "500",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                    whiteSpace: "nowrap",
                    zIndex: 1000,
                  }}
                >
                  {stop.name}
                </div>
              </div>
            </OverlayView>
            <Marker
              position={{
                lat: parseFloat(stop.latitude),
                lng: parseFloat(stop.longitude),
              }}
              title={stop.name}
            />
          </React.Fragment>
        ))}

        {vehicleLocations.map((vehicle) => {
          const vehicleData = vehicles.find((v) => v.device_id === vehicle.deviceId) || {};
          const vehicleIcon = getVehicleIcon(vehicleData.status);

          return (
            vehicle &&
            vehicle.latitude &&
            vehicle.longitude && (
              <React.Fragment key={vehicle.deviceId}>
                <OverlayView
                  position={{
                    lat: parseFloat(vehicle.latitude),
                    lng: parseFloat(vehicle.longitude),
                  }}
                  mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                >
                  <div
                    style={{
                      position: "relative",
                      top: "-120px",
                      left: "0px",
                      width: "2px",
                      height: "2px",
                    }}
                  >
                    <div
                      style={{
                        position: "absolute",
                        width: "2px",
                        height: "102px",
                        top: "15px",
                        backgroundColor: getBackgroundColor(vehicle.deviceId),
                        bottom: "9px",
                        left: "50%",
                        transform: "translateX(-50%)",
                      }}
                    />
                    <div
                      style={{
                        position: "absolute",
                        top: "-10px",
                        left: "50%",
                        transform: "translateX(-50%)",
                        color: "rgb(0, 0, 0)",
                        fontSize: "10px",
                        fontWeight: 600,
                        fontFamily: "Arial, sans-serif",
                        lineHeight: "14.06px",
                        padding: "8px",
                        textAlign: "left",
                        whiteSpace: "nowrap",
                        backgroundColor: getBackgroundColor(vehicle.deviceId),
                        borderRadius: "12px",
                        zIndex: 1000,
                        border: "1px solid black",
                      }}
                    >
                      {vehicle.deviceId}
                      <span style={{ fontWeight: "bold" }}>{getDirectionArrow(vehicle.deviceId)}</span>
                    </div>
                  </div>
                </OverlayView>
                <OverlayView
                  position={{
                    lat: parseFloat(vehicle.latitude),
                    lng: parseFloat(vehicle.longitude),
                  }}
                  mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                >
                  <div
                    style={{
                      width: "40px",
                      height: "40px",
                      position: "relative",
                      transform: `translate(-20px, -20px) ${
                        vehicleDirections[vehicle.deviceId] === "dest_to_source" ? "scaleX(-1)" : ""
                      }`,
                      backgroundImage: `url(${vehicleIcon})`,
                      backgroundSize: "contain",
                      backgroundRepeat: "no-repeat",
                      backgroundPosition: "center",
                    }}
                  />
                </OverlayView>
              </React.Fragment>
            )
          );
        })}

        {directions.map((direction, index) => (
          <DirectionsRenderer
            key={index}
            directions={direction}
            options={{
              suppressMarkers: true,
              preserveViewport: true,
              polylineOptions: {
                strokeColor: direction.routeColor || "#000000",
                strokeOpacity: 0.8,
                strokeWeight: 4,
              },
            }}
          />
        ))}
      </GoogleMap>
    </div>
  );
}

export default React.memo(Map);